<script>
import { mapState } from "vuex";
import { USER_MODULE } from "@/state/public/user.state";
import { userMethods } from "@/state/public";
import Footer from "./teamDirectoryFooter.vue";

export default {
  page: { title: "Team Directory" },
  components: { Footer },
  async created() {
    await this.getUserData();
    this.loading = false;
  },
  computed: {
    ...mapState(USER_MODULE, ["status"]),

    userUUID() {
      return this.$route.query.id || this.$route.params.id;
    },
    userDetails() {
      return this.$store.state.public.user.details || {};
    },

    companiesArr() {
      const arr = this.userDetails.userCompanies;
      return Array.isArray(arr) ? arr : [];
    },

    currentDataCompany() {
      const item = this.companiesArr[this.selectedIndex];
      return { ...item, company: item?.company || {} };
    },

    totalCompanies() {
      return this.companiesArr.length;
    },

    lastIndex() {
      return Math.max(0, this.totalCompanies - 1);
    },

    hasCompanies() {
      return this.totalCompanies > 0;
    },
  },
  methods: {
    ...userMethods,

    // --- Phone helpers ---
    normalizeDigits(phone) {
      return (phone || "").toString().replace(/\D+/g, "");
    },
    toMsisdn(phone) {
      let d = this.normalizeDigits(phone);
      if (!d) return null;
      if (d.startsWith("60")) return d;
      if (d.startsWith("0")) return "60" + d.slice(1);
      if (/^1\d{8,9}$/.test(d)) return "60" + d;
      return d;
    },
    toE164(phone) {
      let d = this.normalizeDigits(phone);
      if (!d) return null;
      if (d.startsWith("60")) return "+" + d;
      if (d.startsWith("0")) return "+60" + d.slice(1);
      if (d.startsWith("1")) return "+60" + d;
      return "+" + d;
    },
    sendWhatsapp(phone) {
      const msisdn = this.toMsisdn(phone);
      if (!msisdn) return this.makeToast("No phone number");
      const message =
        "Hi, I got your number from Hextar's catalog and wanted to reach out";
      const url = `https://wa.me/${msisdn}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    },
    callPhone(phone) {
      const e164 = this.toE164(phone);
      if (!e164) return this.makeToast("No phone number");
      window.location.href = `tel:${e164}`;
    },
    sendMail(mail) {
      window.location.href = `mailto:${mail}`;
    },
    downloadVCard(member) {
      const fullName = `${member.firstName || ""} ${member.lastName || ""}`.trim();
      const e164 = this.toE164(`${member.phoneCode || ""}${member.phone || ""}`) || "";
      const vCardData = `
BEGIN:VCARD
VERSION:3.0
FN:${fullName}
N:${member.lastName || ''};${member.firstName || ''};;;
TEL;TYPE=CELL:${e164}
EMAIL:${member.email || ''}
END:VCARD
      `.trim();

      const blob = new Blob([vCardData], { type: "text/vcard" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fullName || "contact"}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    makeToast(message) {
      this.$bvToast.toast(message, {
        title: "Copied Success",
        variant: "success",
        solid: true,
      });
    },

    // Pretty URL for display
    prettyUrl(u) {
      try {
        const url = u.startsWith("http") ? new URL(u) : new URL(`https://${u}`);
        const host = url.hostname.replace(/^www\./, "");
        const path =
          url.pathname && url.pathname !== "/"
            ? url.pathname.replace(/\/$/, "")
            : "";
        return `${host}${path}`;
      } catch {
        return u;
      }
    },

    async getUserData() {
      if (this.userUUID) {
        await this.getUser({uuid: this.userUUID})
          .then((result) => {
            this.userLoaded = !!result?.success;
          });
      }
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => this.makeToast("Copied to clipboard"))
        .catch((err) => console.error("Failed to copy: ", err));
    },
    prevCompany() {
      if (this.selectedIndex > 0) this.selectedIndex--;
    },
    nextCompany() {
      if (this.selectedIndex < this.lastIndex) this.selectedIndex++;
    },
  },
  data() {
    return {
      loading: true,
      userLoaded: false,
      selectedIndex: 0,
    };
  },
};
</script>

<template>
  <div>
    <div v-if="loading || !status.detail" class="d-flex justify-content-center p-5 m-5">
      <b-spinner variant="info" role="status" class="m-5" />
    </div>
    <b-alert v-else-if="!userLoaded" show variant="warning" class="text-center">
      Team member information is unavailable. Please open a valid team member link.
    </b-alert>
    <div v-else class="container" style="min-height: 70vh; background-color: white; padding-bottom: 20px;">
      <!-- Banner WITH bottom grey line -->
      <div class="header text-center">
        <img
          src="https://ihextar.s3.ap-southeast-1.amazonaws.com/CO_Final_40th+Anniversary_Logo.png"
          class="w-100 header-banner"
          alt="Banner"
        />
      </div>

      <!-- Avatar BELOW the grey line -->
      <div class="profile-after-line">
        <img
          :src="userDetails.image"
          class="profile-avatar border border-white shadow"
          alt="Profile"
        />
      </div>

      <!-- Name & Icons -->
      <div class="text-center mt-3">
        <h5 class="fw-bold" v-if="userDetails.chineseName">{{ userDetails.chineseName }}</h5>
        <h5 class="fw-bold">{{ userDetails.firstName }} {{ userDetails.lastName }}</h5>
        <div class="d-flex justify-content-center gap-4 mt-3">
          <a href="#" class="text-dark fs-4" @click.prevent="sendWhatsapp(`${userDetails.phoneCode || ''}${userDetails.phone || ''}`)">
            <i class="mdi mdi-whatsapp text-success" style="font-size: 30px;"></i>
          </a>
          <a href="#" class="text-dark fs-4" @click.prevent="sendMail(userDetails.email)">
            <i class="mdi mdi-email" style="font-size: 30px; color: #00B4F0;"></i>
          </a>
          <a href="#" class="text-dark fs-4" @click.prevent="callPhone(`${userDetails.phoneCode || ''}${userDetails.phone || ''}`)">
            <i class="mdi mdi-phone" style="font-size: 30px; color: #00B4F0;"></i>
          </a>
          <a href="#" class="text-dark fs-4" @click.prevent="downloadVCard(userDetails)">
            <i class="mdi mdi-account-plus" style="font-size: 30px; color: #00B4F0;"></i>
          </a>
        </div>
      </div>

      <!-- Company Section -->
      <div class="col-md-8 mx-auto mt-5">
        <!-- If NO companies: show message -->
        <div v-if="!hasCompanies" class="alert alert-warning text-center rounded-3 shadow-sm">
          Contact admin for assign ur company
        </div>

        <!-- If companies exist: show carousel -->
        <div v-else>
          <div class="d-flex align-items-center justify-content-center gap-3">
            <!-- Left Arrow -->
            <button class="btn btn-light rounded-circle shadow" @click="prevCompany" :disabled="selectedIndex === 0">
              <i class="mdi mdi-chevron-left" style="font-size: 24px;"></i>
            </button>

            <!-- Current Company Card -->
            <div
              class="details-card p-4 text-center"
              :style="{
                width: '100%',
                background: `linear-gradient(360deg, #${
                  (currentDataCompany.company.colorCode || 'f8f9fa').replace(/^#/, '')
                } 0%, #${
                  ((currentDataCompany.company.colorCode || 'f8f9fa').replace(/^#/, ''))
                }33 100%)`,
                borderRadius: '16px'
              }"
            >
              <img
                :src="currentDataCompany.company.logoUrl || 'https://ihextar.s3.ap-southeast-1.amazonaws.com/CO_Final_40th+Anniversary_Logo.png'"
                alt="Company Logo"
                class="mb-3"
                style="width: 100px; height: 100px; border-radius: 100%; object-fit: scale-down;"
              />

              <!-- Polished name + URL -->
              <h4
                class="company-name"
                :title="currentDataCompany.company.name"
              >
                <CompanyLink :company="currentDataCompany.company" fallback="—" />
              </h4>

              <a
                v-if="currentDataCompany.company.websiteUrl"
                :href="currentDataCompany.company.websiteUrl"
                class="company-url-chip"
                target="_blank"
                rel="noopener"
                :title="currentDataCompany.company.websiteUrl"
                aria-label="Open company website"
              >
                <i class="mdi mdi-web"></i>
                <span class="url-text">{{ prettyUrl(currentDataCompany.company.websiteUrl) }}</span>
                <i class="mdi mdi-open-in-new external-icon"></i>
              </a>
              <p v-else class="text-muted small mt-1">Website not available</p>

              <!-- Services -->
              <div
                class="mt-3"
                v-if="currentDataCompany.company.companyServices &&
                      currentDataCompany.company.companyServices.length"
              >
                <h6 class="text-uppercase text-muted">Services:</h6>
                <div class="d-flex flex-wrap justify-content-center gap-2 mt-2">
                  <span
                    v-for="(service, idx) in currentDataCompany.company.companyServices"
                    :key="'service-' + idx"
                    class="badge bg-light text-dark px-3 py-2"
                    style="border-radius: 999px; border: 1px solid #ccc;"
                  >
                    {{ service.name }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Right Arrow -->
            <button
              class="btn btn-light rounded-circle shadow"
              @click="nextCompany"
              :disabled="selectedIndex >= lastIndex"
            >
              <i class="mdi mdi-chevron-right" style="font-size: 24px;"></i>
            </button>
          </div>

          <!-- Dot Pagination -->
          <div class="text-center mt-3" v-if="totalCompanies > 1">
            <span
              v-for="(item, index) in companiesArr"
              :key="'dot-' + index"
              class="circle-dot mx-1"
              :class="{ active: selectedIndex === index }"
              @click="selectedIndex = index"
            ></span>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<style scoped>
/* General container sizing */
.container {
  max-width: 900px;
}

/* Banner shows a bottom GREY line */
.header {
  border-bottom: 1px solid #e5e7eb;
}
.header-banner {
  height: 200px;
  object-fit: scale-down;
  display: block;
}

/* Avatar BELOW the grey line */
.profile-after-line {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}
.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border-width: 4px !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
}

/* Company name: bold, clamped to 2 lines for very long names */
.company-name {
  font-weight: 700;
  font-size: 1.25rem; /* ~20px */
  letter-spacing: .2px;
  margin: 0 0 6px 0;
  color: #0f172a; /* slate-900 */
  display: -webkit-box;
  -webkit-line-clamp: 2;  /* clamp to 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* URL chip: subtle pill + ellipsis */
.company-url-chip {
  display: inline-flex;
  align-items: center;
  gap: .4rem;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.65);
  box-shadow: 0 2px 10px rgba(0,0,0,.06) inset, 0 1px 3px rgba(0,0,0,.06);
  backdrop-filter: saturate(160%) blur(4px);
  text-decoration: none;
  max-width: 90%;
  color: #475569; /* slate-600 */
  margin-top: 2px;
}

/* Truncate nicely inside the chip */
.company-url-chip .url-text {
  min-width: 0;             /* required for flex ellipsis */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

/* Icon tweaks */
.company-url-chip .external-icon {
  opacity: .7;
  font-size: 16px;
  margin-left: 2px;
}

/* Hover effect */
.company-url-chip:hover {
  background: rgba(255, 255, 255, 0.85);
  color: #0f172a;
}

/* Dots */
.circle-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d9d9d9;
  cursor: pointer;
}
.circle-dot.active {
  background: #00b4f0;
}

/* Details card image */
.details-card img {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
